import React, { useState, useEffect } from "react";
import { Shield, EyeOff, Fingerprint, Key, Lock, ShieldAlert, Code, BrainCircuit, Bomb, Plus, Edit, Save, X, Link } from "lucide-react";
import {useDevApi} from "../../context/ApiContext.tsx";

export interface DevRef {
    id: number;
    name: string;
    url: string;
}

export interface SecretDashboardProps {}

export const SecretDashboard: React.FC<SecretDashboardProps> = () => {
    const [codePhrase, setCodePhrase] = useState<string>("********");
    const [isDecoding, setIsDecoding] = useState<boolean>(true);
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const [devRefs, setDevRefs] = useState<DevRef[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isAddingNew, setIsAddingNew] = useState<boolean>(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [newRef, setNewRef] = useState<{name: string, url: string}>({ name: "", url: "" });
    const [editingRef, setEditingRef] = useState<{name: string, url: string}>({ name: "", url: "" });

    const devApi = useDevApi();

    // Fun codenames for secret agents
    const secretCodenames = [
        "Shadow Phoenix",
        "Quantum Whisper",
        "Cipher Raven",
        "Eclipse Sentinel",
        "Phantom Protocol",
        "Nebula Wraith",
        "Obsidian Specter"
    ];

    // Fetch dev refs on component mount
    useEffect(() => {
        fetchDevRefs();
    }, []);

    const fetchDevRefs = async () => {
        try {
            setLoading(true);
            const response = await devApi.getDevRefs();
            setDevRefs(response);
            setError(null);
        } catch (err) {
            setError("Failed to load developer references");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Simulate decoding effect for the secret code phrase
    useEffect(() => {
        if (isDecoding) {
            const timer = setTimeout(() => {
                setIsDecoding(false);
                setCodePhrase("UMBRELLA-TANGO-47");
            }, 2500);

            return () => clearTimeout(timer);
        }
    }, [isDecoding]);

    // Show alert message after a delay
    useEffect(() => {
        const timer = setTimeout(() => {
            setShowAlert(true);
        }, 4000);

        return () => clearTimeout(timer);
    }, []);

    // Get random codename
    const getRandomCodename = () => {
        return secretCodenames[Math.floor(Math.random() * secretCodenames.length)];
    };

    // Handle creating a new dev ref
    const handleCreateDevRef = async () => {
        if (!newRef.name || !newRef.url) {
            setError("Name and URL are required");
            return;
        }

        try {
            const response = await devApi.createDevRef(newRef);
            setDevRefs([...devRefs, response]);
            setNewRef({ name: "", url: "" });
            setIsAddingNew(false);
            setError(null);
        } catch (err) {
            setError("Failed to create developer reference");
            console.error(err);
        }
    };

    // Handle editing a dev ref
    const handleEditDevRef = async () => {
        if (!editingRef.name || !editingRef.url || !editingId) {
            setError("Name and URL are required");
            return;
        }

        try {
            const response = await devApi.editDevRef({
                ...editingRef,
                id: editingId
            });

            // Update the list with the edited item
            setDevRefs(devRefs.map(ref =>
                ref.id === editingId ? response : ref
            ));

            setEditingId(null);
            setEditingRef({ name: "", url: "" });
            setError(null);
        } catch (err) {
            setError("Failed to update developer reference");
            console.error(err);
        }
    };

    // Start editing a specific dev ref
    const startEditing = (ref: DevRef) => {
        setEditingId(ref.id);
        setEditingRef({ name: ref.name, url: ref.url });
    };

    // Cancel editing
    const cancelEditing = () => {
        setEditingId(null);
        setEditingRef({ name: "", url: "" });
    };

    // Cancel adding new
    const cancelAddNew = () => {
        setIsAddingNew(false);
        setNewRef({ name: "", url: "" });
    };

    return (
        <div className="space-y-6">
            {/* Dramatic welcome banner */}
            <div className="bg-gradient-to-r from-red-900 via-purple-900 to-blue-900 rounded-lg shadow-xl overflow-hidden">
                <div className="relative p-8 text-center">
                    {/* Animated warning symbols */}
                    <div className="absolute top-0 left-0 p-4">
                        <ShieldAlert className="h-10 w-10 text-yellow-300 animate-pulse" />
                    </div>
                    <div className="absolute top-0 right-0 p-4">
                        <Fingerprint className="h-10 w-10 text-yellow-300 animate-pulse" />
                    </div>

                    <h1 className="text-4xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-red-400 to-pink-400 tracking-tight mb-2">
                        TOP SECRET ACCESS
                    </h1>
                    <p className="text-white text-xl md:text-2xl font-semibold tracking-widest">
                        CLEARANCE LEVEL: ULTRA
                    </p>

                    <div className="mt-6 flex justify-center">
                        <div className="inline-flex items-center px-6 py-3 bg-black bg-opacity-50 border-2 border-red-500 rounded-lg">
                            <Lock className="h-5 w-5 text-red-400 mr-2" />
                            <span className="font-mono text-lg text-red-400 tracking-wider">
                {isDecoding ? "DECRYPTING..." : codePhrase}
              </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Alert message that appears after delay */}
            {showAlert && (
                <div className="bg-yellow-100 border-l-4 border-yellow-500 rounded-md p-4 animate-bounce">
                    <div className="flex items-center">
                        <Shield className="h-6 w-6 text-yellow-600 mr-2" />
                        <p className="text-yellow-700 font-medium">
                            REMINDER: Secret user protocols are in effect. Standard discretion advised.
                        </p>
                    </div>
                </div>
            )}

            {/* Developer References Panel */}
            <div className="bg-slate-800 rounded-lg shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                        <Link className="h-6 w-6 text-cyan-400 mr-2" />
                        <h2 className="text-xl font-bold text-white">Developer References</h2>
                    </div>

                    {!isAddingNew && (
                        <button
                            onClick={() => setIsAddingNew(true)}
                            className="bg-cyan-600 hover:bg-cyan-700 text-white px-3 py-1 rounded-md flex items-center text-sm"
                        >
                            <Plus className="h-4 w-4 mr-1" /> Add New
                        </button>
                    )}
                </div>

                {error && (
                    <div className="bg-red-100 border-l-4 border-red-500 p-3 mb-4 rounded">
                        <p className="text-red-700">{error}</p>
                    </div>
                )}

                {/* Add new DevRef form */}
                {isAddingNew && (
                    <div className="bg-slate-700 rounded-md p-4 mb-4">
                        <h3 className="text-white font-bold mb-2">Add New Reference</h3>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-slate-300 text-sm mb-1">Name</label>
                                <input
                                    type="text"
                                    value={newRef.name}
                                    onChange={(e) => setNewRef({...newRef, name: e.target.value})}
                                    className="w-full px-3 py-2 bg-slate-600 text-white rounded-md"
                                    placeholder="Reference name"
                                />
                            </div>
                            <div>
                                <label className="block text-slate-300 text-sm mb-1">URL</label>
                                <input
                                    type="text"
                                    value={newRef.url}
                                    onChange={(e) => setNewRef({...newRef, url: e.target.value})}
                                    className="w-full px-3 py-2 bg-slate-600 text-white rounded-md"
                                    placeholder="https://example.com"
                                />
                            </div>
                            <div className="flex space-x-2">
                                <button
                                    onClick={handleCreateDevRef}
                                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center"
                                >
                                    <Save className="h-4 w-4 mr-1" /> Save
                                </button>
                                <button
                                    onClick={cancelAddNew}
                                    className="bg-slate-500 hover:bg-slate-600 text-white px-4 py-2 rounded-md flex items-center"
                                >
                                    <X className="h-4 w-4 mr-1" /> Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* DevRefs list */}
                <div className="space-y-3">
                    {loading ? (
                        <p className="text-slate-300">Loading references...</p>
                    ) : devRefs.length === 0 ? (
                        <p className="text-slate-300">No references found. Add your first one!</p>
                    ) : (
                        devRefs.map(ref => (
                            <div key={ref.id} className="bg-slate-700 rounded-md p-3">
                                {editingId === ref.id ? (
                                    <div className="space-y-2">
                                        <div>
                                            <label className="block text-slate-300 text-sm mb-1">Name</label>
                                            <input
                                                type="text"
                                                value={editingRef.name}
                                                onChange={(e) => setEditingRef({...editingRef, name: e.target.value})}
                                                className="w-full px-3 py-2 bg-slate-600 text-white rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-slate-300 text-sm mb-1">URL</label>
                                            <input
                                                type="text"
                                                value={editingRef.url}
                                                onChange={(e) => setEditingRef({...editingRef, url: e.target.value})}
                                                className="w-full px-3 py-2 bg-slate-600 text-white rounded-md"
                                            />
                                        </div>
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={handleEditDevRef}
                                                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md flex items-center text-sm"
                                            >
                                                <Save className="h-4 w-4 mr-1" /> Save
                                            </button>
                                            <button
                                                onClick={cancelEditing}
                                                className="bg-slate-500 hover:bg-slate-600 text-white px-3 py-1 rounded-md flex items-center text-sm"
                                            >
                                                <X className="h-4 w-4 mr-1" /> Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <h3 className="text-white font-bold">{ref.name}</h3>
                                            <a
                                                href={ref.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-cyan-400 hover:text-cyan-300 text-sm"
                                            >
                                                {ref.url}
                                            </a>
                                        </div>
                                        <button
                                            onClick={() => startEditing(ref)}
                                            className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded-md flex items-center text-sm"
                                        >
                                            <Edit className="h-4 w-4 mr-1" /> Edit
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Main dashboard content */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Admin tools panel */}
                <div className="bg-slate-800 rounded-lg shadow-lg p-6">
                    <div className="flex items-center mb-4">
                        <Key className="h-6 w-6 text-blue-400 mr-2" />
                        <h2 className="text-xl font-bold text-white">Admin Tools</h2>
                    </div>
                    <p className="text-slate-300 mb-4">Access your super secret admin tools that definitely don't exist.</p>
                    <div className="space-y-2">
                        <button className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center justify-center transition-colors">
                            <Code className="h-4 w-4 mr-2" />
                            <span>Deploy Secret Code</span>
                        </button>
                        <button className="w-full py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md flex items-center justify-center transition-colors">
                            <BrainCircuit className="h-4 w-4 mr-2" />
                            <span>Neural Override</span>
                        </button>
                    </div>
                </div>

                {/* Secret stats panel */}
                <div className="bg-slate-800 rounded-lg shadow-lg p-6">
                    <div className="flex items-center mb-4">
                        <EyeOff className="h-6 w-6 text-green-400 mr-2" />
                        <h2 className="text-xl font-bold text-white">Covert Stats</h2>
                    </div>
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <span className="text-slate-300">Secret Users:</span>
                            <span className="text-green-400 font-mono">42</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-slate-300">Security Level:</span>
                            <span className="text-green-400 font-mono">MAXIMUM</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-slate-300">Threat Detection:</span>
                            <span className="text-green-400 font-mono">ACTIVE</span>
                        </div>
                    </div>
                </div>

                {/* Agent status panel */}
                <div className="bg-slate-800 rounded-lg shadow-lg p-6">
                    <div className="flex items-center mb-4">
                        <Fingerprint className="h-6 w-6 text-pink-400 mr-2" />
                        <h2 className="text-xl font-bold text-white">Agent Status</h2>
                    </div>
                    <div className="mb-4">
                        <p className="text-slate-300 mb-2">Your Codename:</p>
                        <div className="bg-slate-700 p-3 rounded-md">
                            <p className="text-xl font-mono text-pink-400">{getRandomCodename()}</p>
                        </div>
                    </div>
                    <div className="flex items-center">
                        <div className="h-3 w-3 bg-green-500 rounded-full animate-ping mr-2"></div>
                        <p className="text-green-400">Active and undetected</p>
                    </div>
                </div>
            </div>

            {/* Self-destruct button */}
            <div className="mt-8 text-center">
                <button
                    className="inline-flex items-center px-6 py-3 bg-red-700 hover:bg-red-800 text-white font-bold rounded-full transition-colors shadow-lg"
                    onClick={() => alert("Just kidding! This message will NOT self-destruct.")}
                >
                    <Bomb className="h-5 w-5 mr-2" />
                    <span>SELF DESTRUCT</span>
                </button>
                <p className="mt-2 text-xs text-slate-500">
                    (Don't worry, we're just having fun here)
                </p>
            </div>
        </div>
    );
};